// The catalog's product_type_name can't be trusted on its own to decide
// whether an item is an air filter: the cellphone holder #10000 is typed
// "Panel Filter", the wheel chocks/hood snubber have no type at all, and the
// cleaner kits are titled "Green Filter Part #2001". Titles of non-filter
// items are descriptive, though, so the type is combined with title keywords.
export type ProductKind = 'filter' | 'cleaning-kit' | 'throttle-controller' | 'accessory';

interface ClassifiableProduct {
  title?: string;
  product_type_name?: string | null;
}

const ACCESSORY_TITLE = /holder|stand|chock|snubber|intake tube/i;

export function getProductKind(product: ClassifiableProduct): ProductKind {
  const type = (product.product_type_name ?? '').toLowerCase();
  const title = product.title ?? '';

  if (/pedal power|throttle/i.test(title) || type.includes('throttle')) return 'throttle-controller';
  if (type.includes('clean')) return 'cleaning-kit';
  if (ACCESSORY_TITLE.test(title)) return 'accessory';
  if (type.includes('filter') || /filter/i.test(title)) return 'filter';
  return 'accessory';
}

// Short noun used in titles/descriptions, e.g. "Panel Air Filter".
export function getProductTypeLabel(product: ClassifiableProduct): string {
  switch (getProductKind(product)) {
    case 'cleaning-kit':
      return 'Air Filter Cleaning Kit';
    case 'throttle-controller':
      return 'Throttle Response Controller';
    case 'accessory':
      return '';
    case 'filter': {
      const type = (product.product_type_name ?? '').trim();
      // "Panel Filter" -> "Panel Air Filter"; generic/missing types -> "Air Filter"
      if (!type || /^(none|air filters?)$/i.test(type)) return 'Reusable Air Filter';
      return type.replace(/\bFilter\b/i, 'Air Filter');
    }
  }
}

interface FitmentModel {
  title?: string;
  make?: string | { title?: string; name?: string } | null;
}

interface NamedProduct extends ClassifiableProduct {
  gfu_part_num?: string;
  models?: FitmentModel[];
}

const NAME_EXCEPTIONS: Record<string, string> = { Mclaren: 'McLaren' };

function displayMake(make: FitmentModel['make']): string {
  const raw = (typeof make === 'string' ? make : make?.title || make?.name || '').trim();
  if (raw.length <= 3) return raw.toUpperCase(); // BMW, GMC, VW
  const name = raw.toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
  return NAME_EXCEPTIONS[name] ?? name;
}

// Model titles sometimes contain line breaks or are all caps ("CORVETTE Z06").
// Short all-caps words are kept (GTO, SS, RSR); longer ones are capitalised.
function displayModel(title?: string): string {
  return (title ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b[A-Z]{4,}\b/g, (word) => word.charAt(0) + word.slice(1).toLowerCase());
}

function mostCommon(values: string[]): [string, number] {
  const counts = new Map<string, number>();
  for (const value of values) {
    if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0] ?? ['', 0];
}

// "Ford F150" when one make dominates the fitment rows and one of its models
// is the most common application, "Ford" when only the make dominates,
// otherwise '' - so a title never implies that a filter fitting 900
// vehicles across 20 makes is a single-vehicle part.
export function getPrimaryFitment(models: FitmentModel[] = []): string {
  if (models.length === 0) return '';

  const [make, makeCount] = mostCommon(models.map((m) => displayMake(m.make)));
  if (!make || makeCount / models.length < 0.8) return '';

  const makeModels = models.filter((m) => displayMake(m.make) === make);
  const [model, modelCount] = mostCommon(makeModels.map((m) => displayModel(m.title)));
  if (!model || modelCount / models.length < 0.3) return make;
  // Avoid "Humvee Humvee" / "Harley-Davidson Harley-Davidson" when the model repeats the make
  return model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`;
}

// Catalog titles are often just "Green Filter Part #7050" or carry part
// numbers inline ("Hummer Hood Snubber Set Part #0001 MAS Part #MVS919").
// Returns a descriptive name without part numbers, e.g. "Ford F150 Panel Air Filter".
export function getProductDisplayName(product: NamedProduct): string {
  const title = (product.title ?? '').trim();
  const isGeneric = /^green filter\s*-?\s*part\s*#/i.test(title);

  if (isGeneric || !title) {
    const fitment = getPrimaryFitment(product.models);
    let label = getProductTypeLabel(product);
    // "Harley-Davidson" fitment + "Harley-Davidson Air Filter" type -> say the make once
    const make = fitment.split(' ')[0];
    if (make && label.startsWith(`${make} `)) label = label.slice(make.length + 1);
    const name = [fitment, label].filter(Boolean).join(' ');
    return name || 'Green Filter Product';
  }

  const sku = product.gfu_part_num ?? '';
  return title
    .replace(/\b(?:MAS\s+)?part\s*#\s*\S+/gi, '')
    .replace(sku ? new RegExp(`#?\\b${sku.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g') : /$^/, '')
    .replace(/[*]+/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
